import { useCallback, useEffect, useMemo, useState } from 'react';
import { IShipDateFilterProps } from '../../../types/IFilterEngine';
import { getUniqueShipWindows, isShipDateFilterOn, matchesShipWindows } from './Utils';

export const useShipDateFilter = (items?: any[] | null) => {
  const [selectedShipWindows, setSelectedShipWindows] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const shipWindows = useMemo(() => getUniqueShipWindows(items), [items]);
  // null while the filter is off, every item is kept then
  const selectedWindowKeys = useMemo(
    () => (isShipDateFilterOn(selectedShipWindows) ? new Set(selectedShipWindows) : null),
    [selectedShipWindows]
  );

  // a window the current items no longer offer would filter everything away
  useEffect(() => {
    if (!shipWindows.length) return;
    setSelectedShipWindows((previous: string[]) => {
      if (!previous.length) return previous;
      const availableKeys = new Set(shipWindows.map((shipWindow) => shipWindow.key));
      const kept = previous.filter((key: string) => availableKeys.has(key));
      return kept.length === previous.length ? previous : kept;
    });
  }, [shipWindows]);

  const shipDateFilterProps: IShipDateFilterProps = useMemo(
    () => ({
      shipWindows,
      selectedShipWindows,
      setSelectedShipWindows,
      isDialogOpen,
      openDialog: () => setIsDialogOpen(true),
      closeDialog: () => setIsDialogOpen(false),
    }),
    [shipWindows, selectedShipWindows, isDialogOpen]
  );

  // stable while the selection holds, so it is safe as an effect dependency
  const matchesSelectedShipWindows = useCallback(
    (item: any) => matchesShipWindows(item, selectedWindowKeys),
    [selectedWindowKeys]
  );

  const clearShipDates = useCallback(() => setSelectedShipWindows([]), []);

  return {
    shipDateFilterProps,
    isShipDateFilterOn: isShipDateFilterOn(selectedShipWindows),
    matchesSelectedShipWindows,
    clearShipDates,
  };
};

export default useShipDateFilter;
